import React, { useState, useEffect } from 'react';
import Layout from '../components/dashboard/Layout';
import { authAPI } from '../services/api';
import api from '../services/api';
import { Plus, UserPlus, Shield, Briefcase, Target, User, Trash2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const UsersPage = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const { user: currentUser } = useAuth();
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    role: 'admin',
    phone: ''
  });

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const response = await api.get('/api/users');
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to load users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await authAPI.register(formData);
      alert('User berhasil ditambahkan!');
      setShowAddModal(false);
      setFormData({
        email: '',
        password: '',
        full_name: '',
        role: 'admin',
        phone: ''
      });
      loadUsers();
    } catch (error) {
      alert('Gagal menambahkan user: ' + (error.response?.data?.detail || 'Unknown error'));
    }
  };

  const getRoleInfo = (role) => {
    const roleMap = {
      super_admin: { 
        label: 'Super Admin', 
        icon: Shield, 
        color: 'text-primary bg-primary/10 border-primary/30',
        desc: 'Akses penuh ke semua fitur'
      },
      admin: { 
        label: 'Admin', 
        icon: Briefcase, 
        color: 'text-secondary bg-secondary/10 border-secondary/30',
        desc: 'Kelola assets, clients, invoices'
      },
      marketing: { 
        label: 'Marketing', 
        icon: Target, 
        color: 'text-yellow-500 bg-yellow-500/10 border-yellow-500/30',
        desc: 'Kelola target & pipeline sales'
      },
      client: { 
        label: 'Client', 
        icon: User, 
        color: 'text-muted-foreground bg-muted border-border',
        desc: 'Lihat data rental sendiri'
      }
    };
    return roleMap[role] || roleMap.client;
  };

  // Only super_admin can access this page
  if (currentUser?.role !== 'super_admin') {
    return (
      <Layout>
        <div className="text-center py-12">
          <Shield className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-foreground mb-2">Akses Terbatas</h2>
          <p className="text-muted-foreground">Hanya Super Admin yang dapat mengelola users</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground mb-2">User Management</h1>
            <p className="text-muted-foreground">Kelola akses karyawan dan admin</p>
          </div>
          <button
            onClick={() => setShowAddModal(true)}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors flex items-center space-x-2 btn"
            data-testid="add-user-button"
          >
            <UserPlus className="w-4 h-4" />
            <span>Tambah User</span>
          </button>
        </div>

        {/* User Grid */}
        {loading ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground">Loading users...</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {users.map((user) => {
              const roleInfo = getRoleInfo(user.role);
              const RoleIcon = roleInfo.icon;
              
              return (
                <div
                  key={user.id}
                  className="glass-card p-6 rounded-xl border border-border/40"
                  data-testid="user-card"
                >
                  <div className="flex items-start space-x-4">
                    <div className={`p-3 rounded-lg border ${roleInfo.color}`}>
                      <RoleIcon className="w-6 h-6" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-foreground mb-1">{user.full_name}</h3>
                      <p className="text-sm text-muted-foreground mb-3">{user.email}</p>
                      
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-muted-foreground">Role:</span>
                          <span className={`px-2 py-1 rounded text-xs font-medium border ${roleInfo.color}`}>
                            {roleInfo.label}
                          </span>
                        </div>
                        {user.phone && (
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-muted-foreground">Phone:</span>
                            <span className="text-xs text-foreground">{user.phone}</span>
                          </div>
                        )}
                      </div>
                      
                      <p className="text-xs text-muted-foreground mt-3">{roleInfo.desc}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Add User Modal */}
        {showAddModal && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="glass-card p-6 rounded-xl border border-border/40 max-w-md w-full">
              <h2 className="text-2xl font-bold text-foreground mb-4">Tambah User Baru</h2>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-foreground/80 mb-2">
                    Nama Lengkap
                  </label>
                  <input
                    type="text"
                    value={formData.full_name}
                    onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                    className="w-full px-4 py-3 bg-muted/50 border border-border rounded-lg text-foreground focus:outline-none focus:border-primary transition-colors"
                    placeholder="John Doe"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground/80 mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="w-full px-4 py-3 bg-muted/50 border border-border rounded-lg text-foreground focus:outline-none focus:border-primary transition-colors"
                    placeholder="user@example.com"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground/80 mb-2">
                    Password
                  </label>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    className="w-full px-4 py-3 bg-muted/50 border border-border rounded-lg text-foreground focus:outline-none focus:border-primary transition-colors"
                    placeholder="Minimal 8 karakter"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground/80 mb-2">
                    Phone (Opsional)
                  </label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    className="w-full px-4 py-3 bg-muted/50 border border-border rounded-lg text-foreground focus:outline-none focus:border-primary transition-colors"
                    placeholder="+62812345678"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground/80 mb-2">
                    Role / Akses
                  </label>
                  <select
                    value={formData.role}
                    onChange={(e) => setFormData({...formData, role: e.target.value})}
                    className="w-full px-4 py-3 bg-muted/50 border border-border rounded-lg text-foreground focus:outline-none focus:border-primary transition-colors"
                    required
                  >
                    <option value="admin">Admin - Kelola operasional</option>
                    <option value="marketing">Marketing - Kelola sales</option>
                    <option value="super_admin">Super Admin - Akses penuh</option>
                    <option value="client">Client - Akses terbatas</option>
                  </select>
                </div>

                <div className="flex space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowAddModal(false)}
                    className="flex-1 px-4 py-3 bg-muted text-foreground rounded-lg hover:bg-muted/80 transition-colors"
                  >
                    Batal
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors btn"
                  >
                    Tambah User
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default UsersPage;
